import { useEffect, useMemo, useState } from 'react';
import { api, ErroApi, type ConfigItem, type ConfigView } from '../../api';

/**
 * Configurações/integrações do Conti9 (só admin) — padrão da casa.
 *
 * Abas por grupo com ponto de "não salvo", teste por grupo, aba "Servidor"
 * só-leitura. SEGREDO fica no .env do servidor (aparece só como "configurado");
 * o resto é editável.
 */

const ABA_SERVIDOR = '__servidor__';
const TESTE_DO_GRUPO: Record<string, string> = { 'Mensageria (WhatsApp)': 'evolution' };

const CHIP = {
  painel: { bg: 'var(--accent-soft)', fg: 'var(--accent-light)', txt: 'painel' },
  env: { bg: 'var(--status-duplicado-bg)', fg: 'var(--text-muted)', txt: '.env' },
  unset: { bg: 'var(--status-duplicado-bg)', fg: 'var(--text-faint)', txt: 'não definido' },
};
function Chip({ origem }: { origem: ConfigItem['origem'] }) {
  const c = CHIP[origem] ?? CHIP.unset;
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: c.bg, color: c.fg }}>{c.txt}</span>;
}

export function ConfiguracoesSistema() {
  const [view, setView] = useState<ConfigView | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [aba, setAba] = useState('');
  const [testes, setTestes] = useState<Record<string, { ok: boolean; detalhe: string } | 'rodando'>>({});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');

  async function carregar() {
    setErro('');
    try { setView(await api.getConfig()); setEdits({}); }
    catch (e) { setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar.'); }
  }
  useEffect(() => { carregar(); }, []);

  const abas = useMemo(() => [...(view?.groups ?? []).map((g) => g.group), ABA_SERVIDOR], [view]);
  const abaAtual = aba || abas[0] || '';
  const grupoAtivo = view?.groups.find((g) => g.group === abaAtual) ?? null;
  const alvoTeste = TESTE_DO_GRUPO[abaAtual];
  const totalPendentes = Object.keys(edits).length;

  function pendentesDoGrupo(nome: string): number {
    const g = view?.groups.find((x) => x.group === nome);
    return g ? g.items.filter((i) => i.key in edits).length : 0;
  }

  async function salvar() {
    if (totalPendentes === 0) return;
    setSalvando(true); setErro(''); setOk('');
    try {
      const r = await api.salvarConfig(edits);
      setView({ groups: r.groups, locked: r.locked });
      setEdits({});
      setOk(`${r.alterados.length} parâmetro(s) salvos.`);
    } catch (e) { setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.'); }
    finally { setSalvando(false); }
  }

  async function usarEnv(item: ConfigItem) {
    if (!confirm(`Voltar "${item.label}" para o valor do .env?`)) return;
    setSalvando(true); setErro('');
    try {
      const r = await api.salvarConfig({ [item.key]: null });
      setView({ groups: r.groups, locked: r.locked });
      setEdits((s) => { const n = { ...s }; delete n[item.key]; return n; });
    } catch (e) { setErro(e instanceof ErroApi ? e.message : 'Não consegui restaurar.'); }
    finally { setSalvando(false); }
  }

  async function testar(alvo: string) {
    setTestes((t) => ({ ...t, [alvo]: 'rodando' }));
    try {
      if (totalPendentes > 0) await salvar();
      const r = await api.testarIntegracao(alvo);
      setTestes((t) => ({ ...t, [alvo]: r }));
    } catch (e) {
      setTestes((t) => ({ ...t, [alvo]: { ok: false, detalhe: e instanceof ErroApi ? e.message : 'Falhou.' } }));
    }
  }

  function resultado(alvo: string) {
    const r = testes[alvo];
    if (!r || r === 'rodando') return null;
    return (
      <span className="field-hint" style={{ margin: 0, color: r.ok ? 'var(--status-aceito-fg)' : 'var(--status-rejeitado-fg)' }}>
        {r.ok ? '✓' : '✗'} {r.detalhe}
      </span>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h1 style={{ margin: 0 }}>Configurações</h1>
          <p className="field-hint" style={{ margin: '4px 0 0' }}>
            Chaves e tokens ficam no .env do servidor — aqui aparecem só como "configurado". O resto é editável.
          </p>
        </div>
        <button className="btn btn-primary" style={{ width: 'auto' }} onClick={salvar} disabled={salvando || totalPendentes === 0}>
          {salvando ? 'Salvando…' : totalPendentes > 0 ? `Salvar ${totalPendentes}` : 'Salvar'}
        </button>
      </div>

      {erro && <p className="error-msg">{erro}</p>}
      {ok && <p className="field-hint" style={{ color: 'var(--status-aceito-fg)', fontWeight: 700 }}>{ok}</p>}

      {!view ? <p className="field-hint">Carregando…</p> : (
        <>
          <div className="tabs">
            {abas.map((g) => (
              <button key={g} type="button" className={`tab ${abaAtual === g ? 'active' : ''}`} onClick={() => setAba(g)}>
                {g === ABA_SERVIDOR ? 'Servidor' : g}
                {pendentesDoGrupo(g) > 0 && <span className="tab-dot" title="não salvo" />}
              </button>
            ))}
          </div>

          <div className="cfg-card">
            {abaAtual === ABA_SERVIDOR ? (
              <>
                <p className="field-hint" style={{ marginTop: 0 }}>Definidos no servidor (só no .env). Não são editáveis por aqui.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {view.locked.map((l) => (
                    <span key={l.key} style={{ fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: l.configured ? 'var(--status-aceito-bg)' : 'var(--status-duplicado-bg)', color: l.configured ? 'var(--status-aceito-fg)' : 'var(--text-faint)' }}>
                      {l.configured ? '✓' : '—'} {l.label}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary" style={{ width: 'auto' }} onClick={() => testar('database')} disabled={testes.database === 'rodando'}>
                    {testes.database === 'rodando' ? 'Testando…' : 'Testar banco'}
                  </button>
                  {resultado('database')}
                  <button className="btn btn-secondary" style={{ width: 'auto' }} onClick={() => testar('r2')} disabled={testes.r2 === 'rodando'}>
                    {testes.r2 === 'rodando' ? 'Testando…' : 'Testar R2'}
                  </button>
                  {resultado('r2')}
                </div>
              </>
            ) : (
              <>
                {alvoTeste && (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18, paddingBottom: 18, borderBottom: '1px solid var(--divider)' }}>
                    <span className="field-hint" style={{ margin: 0, flex: '1 1 12rem' }}>"Configurado" não é "funcionando" — teste antes de confiar.</span>
                    <button className="btn btn-secondary" style={{ width: 'auto' }} onClick={() => testar(alvoTeste)} disabled={testes[alvoTeste] === 'rodando'}>
                      {testes[alvoTeste] === 'rodando' ? 'Testando…' : 'Testar conexão'}
                    </button>
                    {resultado(alvoTeste)}
                  </div>
                )}

                {grupoAtivo?.items.map((item) => (
                  <div key={item.key} style={{ marginBottom: 18 }}>
                    <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {item.label}
                      <Chip origem={item.origem} />
                      {item.kind === 'secret' && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: item.configured ? 'var(--status-aceito-bg)' : 'var(--status-duplicado-bg)', color: item.configured ? 'var(--status-aceito-fg)' : 'var(--text-faint)' }}>
                          {item.configured ? `configurado ${item.preview}`.trim() : 'não configurado'}
                        </span>
                      )}
                      {item.overridden && (
                        <button type="button" onClick={() => usarEnv(item)} disabled={salvando}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11.5, color: 'var(--text-faint)', textDecoration: 'underline', padding: 0 }}>usar o .env</button>
                      )}
                    </label>

                    {item.kind === 'secret' ? (
                      <span className="field-hint">Segredo: edite <code>{item.envVar}</code> no .env do servidor.{item.hint ? ` ${item.hint}` : ''}</span>
                    ) : (
                      <>
                        <input className="input" type={item.kind === 'number' ? 'number' : 'text'}
                          value={item.key in edits ? edits[item.key] : item.value}
                          onChange={(e) => setEdits((s) => ({ ...s, [item.key]: e.target.value }))} />
                        {item.hint && <span className="field-hint">{item.hint}</span>}
                      </>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
